import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { COUNCIL_REVIEWER_SLUGS } from '@death-star/shared';
import type { Concept } from './types.js';

export const concepts: Concept[] = [
  // ─── Flow ────────────────────────────────────────────────
  {
    slug: 'verdict',
    name: 'Verdict',
    group: 'flow',
    oneLiner: "The final disposition the council emits for a PR — APPROVE, NEEDS_HUMAN, or REQUEST_CHANGES — computed from all reviewer findings against policy.",
    seenOn: ['/repos/:org/:repo/pr/:pr', '/calibration', '/scorecard'],
    related: ['finding', 'council', 'policy-version'],
    expanded: (
      <>
        <p>
          The runner Zod-parses the verdict JSON emitted by the <code>dark-council</code> skill.
          The schema is <strong>frozen</strong> — changes require coordinated PRs across
          <code> rewind-corp/diff-vader </code> and <code> rewind-corp/engineering-plugins</code>.
        </p>
        <p>
          The verdict carries the policy version it was computed under, so historical verdicts
          remain explainable after central.yml changes.
        </p>
      </>
    ),
    externalRefs: [
      { label: 'CouncilVerdictSchema', href: 'https://github.com/rewind-corp/diff-vader/blob/main/packages/types/src/council.ts' },
    ],
  },
  {
    slug: 'finding',
    name: 'Finding',
    group: 'flow',
    oneLiner: 'A single observation a reviewer makes about the diff — severity, confidence, category, and body. Multiple findings roll up into a verdict.',
    seenOn: ['/repos/:org/:repo/pr/:pr', '/calibration', '/suppressions'],
    related: ['reviewer', 'verdict', 'suppression'],
    expanded: (
      <>
        <p>
          Findings are the unit of work for the council. The verdict-engine aggregates them
          (counting by severity, applying suppressions, checking hard rules) to compute the verdict.
        </p>
        <p>
          A finding&apos;s <em>substantive</em> flag distinguishes actionable observations from
          advisory noise — used in calibration drift metrics.
        </p>
      </>
    ),
  },
  {
    slug: 'council',
    name: 'Council',
    group: 'flow',
    oneLiner: "The set of reviewer skills configured to review a given PR. Membership is defined per-tier in central.yml.",
    seenOn: ['/calibration', '/policy-versions'],
    related: ['reviewer', 'tier', 'central-yml'],
    expanded: (
      <>
        <p>
          The council is not a fixed list — it&apos;s whatever reviewers a repo&apos;s tier activates. HIGH-tier
          repos get the full council; LOW-tier ones a subset. The council orchestrator (<code>dark-council</code>)
          dispatches reviewers in parallel and merges their findings.
        </p>
      </>
    ),
  },
  {
    slug: 'reviewer',
    name: 'Reviewer',
    group: 'flow',
    oneLiner: 'A specialised skill that inspects a PR and emits findings — e.g. correctness, test-adequacy, performance, supply-chain.',
    seenOn: ['/calibration', '/policy-versions'],
    related: ['council', 'finding', 'tier'],
    expanded: (
      <>
        <p>
          Active reviewers:{' '}
          {COUNCIL_REVIEWER_SLUGS.map((slug, i) => (
            <Fragment key={slug}>
              {i > 0 && ', '}
              <code>{slug}</code>
            </Fragment>
          ))}.
          Each lives in <code>rewind-corp/engineering-plugins/plugins/diff-vader/skills/</code>.
        </p>
      </>
    ),
    externalRefs: [
      { label: 'engineering-plugins/diff-vader', href: 'https://github.com/rewind-corp/engineering-plugins/tree/main/plugins/diff-vader/skills' },
    ],
  },

  // ─── Config ──────────────────────────────────────────────
  {
    slug: 'tier',
    name: 'Tier (HIGH / MEDIUM / LOW)',
    group: 'config',
    oneLiner: "A repo's risk classification. Controls which reviewers run and how strict the verdict is.",
    seenOn: ['/policy-versions', '/config', '/repos'],
    related: ['council', 'central-yml', 'mode'],
    expanded: (
      <>
        <p>
          HIGH = whole-repo-sensitive (core engine, billing). MEDIUM = standard production. LOW =
          pre-production / playground. The org&apos;s <code>tier_floor</code> in central.yml is the default;
          individual repos can override via a central.yml entry.
        </p>
        <p>
          Trust path globs (hard rules) to do safety work — pre-production repos can sit at MEDIUM.
        </p>
      </>
    ),
  },
  {
    slug: 'mode',
    name: 'Mode (disabled / shadow / enabled)',
    group: 'config',
    oneLiner: "Per-repo runtime state: disabled = no dispatch, shadow = run + comment but never gate, enabled = run and gate the PR.",
    seenOn: ['/config'],
    related: ['dv-config', 'tier'],
    expanded: (
      <>
        <p>
          Modes live in <code>dv-config</code> (DynamoDB), not central.yml — so flipping a repo
          to shadow or pausing it does not require a PR. The dispatcher Lambda reads the effective
          mode via <code>getEffectiveDvConfig</code> on every PR event.
        </p>
      </>
    ),
  },
  {
    slug: 'central-yml',
    name: 'central.yml',
    group: 'config',
    oneLiner: "The CODEOWNERS-gated override file in rewind-corp/diff-vader. Holds explicit per-org / per-repo policy choices on top of the safe defaults the runner synthesizes for any opted-in org.",
    seenOn: ['/policy-versions', '/config'],
    related: ['dv-config', 'policy-source', 'policy-version', 'hard-rule', 'tier'],
    expanded: (
      <>
        <p>
          Changes require a PR with <code>@rewind-corp/platform</code> co-approval. Loaded fresh
          by the runner on every workflow run, so changes take effect on the next PR review.
        </p>
        <p>
          An org doesn&apos;t <em>need</em> a central.yml entry to run Diff Vader — opt-in via
          the dv-config page is enough. The runner synthesizes a safe default (MEDIUM floor,
          general council, no auto-approve) for any opted-in org without an explicit entry. A
          central.yml block is the <strong>audit receipt</strong> that says &quot;a human picked
          this shape on purpose.&quot; Without one, the config page shows the org as{' '}
          <code>synthesized — needs policy audit</code>.
        </p>
        <p>
          Most repos still need <strong>no entry</strong> — they inherit the org&apos;s defaults.
          Only override needs (different tier, repo-local hard rules, custom council) warrant a PR.
        </p>
      </>
    ),
    externalRefs: [
      { label: 'policies/central.yml', href: 'https://github.com/rewind-corp/diff-vader/blob/main/policies/central.yml' },
    ],
  },
  {
    slug: 'dv-config',
    name: 'dv-config',
    group: 'config',
    oneLiner: "Per-org and per-repo runtime state in death-star's DynamoDB — opt-in, mode, rerunOnPush, and the policy-source badge. Edited through the portal, no PR needed.",
    seenOn: ['/config'],
    related: ['mode', 'central-yml', 'policy-source'],
    expanded: (
      <>
        <p>
          Onboarding a new org is <strong>two steps</strong>: install the diff-vader App on the
          org, and toggle opt-in here. The runner synthesizes a safe default policy for the first
          review; a central.yml PR is only needed if you want to deviate from the defaults
          (e.g. raise the tier floor or add a custom hard-rule path).
        </p>
      </>
    ),
  },
  {
    slug: 'policy-source',
    name: 'Policy source',
    group: 'config',
    oneLiner: "Per-org/per-repo badge on the dv-config page indicating whether the policy comes from an explicit central.yml entry, an org-level inheritance, or runner-synthesized defaults.",
    seenOn: ['/config'],
    related: ['central-yml', 'dv-config'],
    expanded: (
      <>
        <p>Four states the badge reports:</p>
        <ul>
          <li>
            <strong>customized</strong> (green) — explicit entry in central.yml. Treat as an
            audit receipt: a human picked this shape on purpose.
          </li>
          <li>
            <strong>inherits org default</strong> (gray) — repo-level state; the repo has no
            central.yml block, so it uses the org&apos;s defaults. Common and expected.
          </li>
          <li>
            <strong>synthesized — needs policy audit</strong> (yellow) — org is opted in via
            dv-config but has no central.yml entry. The runner is using safe defaults (MEDIUM
            floor, general council, no auto-approve). Click &quot;Show orgs needing policy
            audit&quot; to filter the list to just these rows.
          </li>
          <li>
            <strong>awaiting first review</strong> (gray) — opted in but Diff Vader has not run
            yet on any PR. Policy source is unknown until the first review fires the
            <code>policy.resolution</code> event.
          </li>
        </ul>
        <p>
          Yellow rows are the audit candidates: someone enrolled the org but nobody has ratified
          the defaults yet. Either confirm the defaults are right and add a PR to central.yml
          with the same shape (turning the row green), or pause the org if it shouldn&apos;t
          have been enrolled.
        </p>
      </>
    ),
  },
  {
    slug: 'hard-rule',
    name: 'Hard rule (always-human paths)',
    group: 'config',
    oneLiner: "A path glob in central.yml's hard_rules.always_human.paths that forces human review regardless of council verdict.",
    seenOn: ['/policy-versions'],
    related: ['central-yml', 'verdict'],
    expanded: (
      <>
        <p>
          Used for compliance-sensitive or change-control-restricted paths (migrations,
          IAM, deployment config). Hard rules override an APPROVE verdict — if any matched
          file appears in the diff, the verdict downgrades to NEEDS_HUMAN.
        </p>
      </>
    ),
  },
  {
    slug: 'suppression',
    name: 'Suppression',
    group: 'config',
    oneLiner: 'An explicit "do not surface this finding pattern on this repo" entry. Lets operators silence known-noisy findings without disabling whole reviewers.',
    seenOn: ['/suppressions'],
    related: ['finding', 'reviewer'],
    expanded: (
      <>
        <p>
          Suppressions are scoped (org, repo, reviewer, pattern) and have an expiry so they
          don&apos;t linger forever. The verdict-engine consults them when rolling up findings —
          a suppressed finding is dropped from the count but stays visible in the raw output
          for audit.
        </p>
      </>
    ),
  },

  // ─── Tracked ─────────────────────────────────────────────
  {
    slug: 'calibration',
    name: 'Calibration',
    group: 'tracked',
    oneLiner: "Tracking how often the council's verdict matches what humans actually did after the PR closed.",
    seenOn: ['/calibration', '/repos/:org/:repo', '/scorecard'],
    related: ['drift', 'settlement', 'reviewer'],
    expanded: (
      <>
        <p>
          Calibration surfaces three views: per-reviewer (does this reviewer find real bugs?),
          per-council (does the aggregate verdict track human disposition?), and drift (have
          the metrics shifted vs. baseline?).
        </p>
      </>
    ),
  },
  {
    slug: 'drift',
    name: 'Drift',
    group: 'tracked',
    oneLiner: "A statistically significant shift in a reviewer's recent metrics (findings_per_run, pct_substantive, thumbs_down_ratio) vs its baseline.",
    seenOn: ['/calibration'],
    related: ['calibration', 'baseline', 'reviewer'],
    expanded: (
      <>
        <p>
          Drift alerts have warning / critical severity and a delta percentage. They&apos;re a leading
          indicator that a reviewer&apos;s behaviour has changed — usually because of a skill update,
          model swap, or content drift in the diffs reaching it.
        </p>
      </>
    ),
  },
  {
    slug: 'settlement',
    name: 'Settlement',
    group: 'tracked',
    oneLiner: 'Once a PR closes, death-star "settles" the verdict against the final outcome (merged, closed-no-merge, hot-fixed, reverted). Drives calibration.',
    seenOn: ['/calibration'],
    related: ['calibration', 'verdict'],
    expanded: (
      <>
        <p>
          Settlement happens asynchronously after PR close, with a lookback window for hot-fixes
          and reverts. A verdict that said APPROVE but landed in a hot-fix later counts against
          calibration; a NEEDS_HUMAN that merged cleanly with no changes counts as over-cautious.
        </p>
      </>
    ),
  },
  {
    slug: 'policy-version',
    name: 'Policy version',
    group: 'tracked',
    oneLiner: 'A snapshot of central.yml at the moment a verdict was computed, so historical verdicts can be re-explained after policy changes.',
    seenOn: ['/policy-versions'],
    related: ['central-yml', 'verdict'],
    expanded: (
      <>
        <p>
          Each version is keyed by commit SHA of central.yml. The portal renders diffs between
          versions so operators can see exactly what changed and when.
        </p>
      </>
    ),
  },
  {
    slug: 'baseline',
    name: 'Baseline',
    group: 'tracked',
    oneLiner: 'A backfilled measurement window (from BASELINE_SINCE_ISO = 2026-01-01) used as the "before AI review" comparison for velocity and drift.',
    seenOn: ['/scorecard', '/calibration'],
    related: ['calibration', 'drift'],
    expanded: (
      <>
        <p>
          Baselines are computed by <code>backfill-baselines.ts</code> and stored per-repo. The
          window starts at the constant <code>BASELINE_SINCE_ISO</code> and runs to the row&apos;s
          <code> computed_at</code>. The velocity scorecard derives &quot;PRs per week&quot; from the
          baseline merged_count and window length.
        </p>
      </>
    ),
  },

  // ─── Operator ────────────────────────────────────────────
  {
    slug: 'weekly-digest',
    name: 'Weekly digest',
    group: 'operator',
    oneLiner: "A scheduled summary posted to Slack and viewable in the portal, surfacing the week's findings, calibration shifts, and notable incidents.",
    seenOn: ['/digest'],
    related: ['calibration', 'incidents', 'reactions'],
    expanded: (
      <>
        <p>
          The digest is generated by <code>aggregate-weekly-digest.ts</code> and posted via the
          dispatcher on a cron. The portal view shows the same content plus drill-down links
          into specific PRs and reviewer pages.
        </p>
      </>
    ),
  },
  {
    slug: 'velocity-scorecard',
    name: 'Velocity scorecard',
    group: 'operator',
    oneLiner: 'Per-repo and per-org metrics tracking prs_merged_30d, auto_approval_rate_30d, and ttv_median_seconds_30d, with deltas vs. baseline.',
    seenOn: ['/scorecard', '/repos/:org/:repo'],
    related: ['baseline', 'calibration'],
    expanded: (
      <>
        <p>
          Each metric carries a status (green / yellow / red) plus a delta percentage. The org
          and per-repo endpoints share their computation through a pure helper to keep them
          consistent.
        </p>
      </>
    ),
  },
  {
    slug: 'incidents',
    name: 'Incidents',
    group: 'operator',
    oneLiner: "Production incidents (from PagerDuty / Confluence postmortems) linked to PRs the council reviewed — used to ask 'did we miss anything?'",
    seenOn: ['/incidents'],
    related: ['calibration', 'verdict'],
    expanded: (
      <>
        <p>
          Linkage is heuristic (time window + author + path overlap) and operator-confirmable.
          Linked incidents that trace to APPROVE verdicts are the most interesting signal —
          they&apos;re the council&apos;s false negatives.
        </p>
      </>
    ),
  },
  {
    slug: 'reactions',
    name: 'Reactions',
    group: 'operator',
    oneLiner: "👍 / 👎 emoji reactions PR authors add to council comments. Polled hourly — GitHub does not fire webhooks for reactions.",
    seenOn: ['/digest'],
    related: ['weekly-digest', 'calibration'],
    expanded: (
      <>
        <p>
          See <Link to="/about/reactions">/about/reactions</Link> for the full explainer
          including how reactions feed into calibration and what each emoji means.
        </p>
      </>
    ),
  },
  {
    slug: 'slicing',
    name: 'Slicing',
    group: 'operator',
    oneLiner: 'A dashboard that breaks a single metric (auto-approval, time-to-merge, revert rate, etc.) down by PR shape — size, path category, change type, author type, primary language.',
    seenOn: ['/slicing'],
    related: ['velocity-scorecard', 'verdict', 'calibration'],
    expanded: (
      <>
        <p>
          Slicing answers &quot;where is this metric coming from?&quot; Pick a metric, pick a window
          (7d / 30d / 90d / since-2026-01-01), and the dashboard renders the current value plus a
          per-bucket breakdown across each dimension — so you can spot, say, that auto-approval
          drops on large diffs or on migrations.
        </p>
        <p>
          Buckets with a small sample size are flagged with a note rather than over-interpreted.
          The same numbers feed the <Link to="/scorecard">velocity scorecard</Link> headline rows
          and the weekly digest&apos;s movement callouts.
        </p>
      </>
    ),
  },
  {
    slug: 'new-authors',
    name: 'New authors',
    group: 'operator',
    oneLiner: 'A view of authors making their first PR after diff-vader was enabled on their repo. Spots when AI review changes onboarding shape.',
    seenOn: ['/repos'],
    related: ['velocity-scorecard'],
    expanded: (
      <>
        <p>
          New-author detection runs nightly. The view distinguishes &quot;first PR ever to the repo&quot;
          from &quot;first PR after diff-vader enabled,&quot; so we don&apos;t conflate a longstanding contributor
          with a genuine newcomer.
        </p>
      </>
    ),
  },
];
